export class ShippingOption {
  carrier: string;
  serviceLevel: string;
  cost: number;
  /**
   * Legacy single-number expected transit time (in days). Populated
   * from the pre-2026 `packageandshippingcomputation/shippingoptions`
   * response when the server emits it. Superseded by the
   * {@link deliveryDaysMin} / {@link deliveryDaysMax} range fields —
   * kept for backward compatibility so a legacy response that only
   * carries this single value still renders in the UI (we synthesize
   * a range of `[deliveryDays, deliveryDays]` from it).
   *
   * `null` / `undefined` means the server did not provide any transit
   * estimate for this rate row.
   */
  deliveryDays: number;
  /**
   * Earliest expected transit day for this rate row (business days,
   * inclusive). Together with {@link deliveryDaysMax} forms the range
   * the UI displays as e.g. "3–5 business days". Populated by the
   * server-side rating call from the underlying carrier API's
   * `min_transit_time` / `max_transit_time` (Shippo, EasyPost, etc.).
   *
   * `null` / `undefined` when the carrier does not publish a range for
   * this service level.
   */
  deliveryDaysMin?: number;
  /** Latest expected transit day (business days, inclusive). See {@link deliveryDaysMin}. */
  deliveryDaysMax?: number;
  /**
   * Server-issued opaque signature covering this `(carrier, serviceLevel,
   * facilityId, packageSetId, packages)` tuple, returned by
   * `POST order/rateshipping` (S-3). Echoed in the place-order envelope under
   * `signatures.shipping` so the server can skip a duplicate Shippo/EasyPost
   * call. Empty when this option came from the legacy two-call shipping flow
   * (no signature emitted there) or before the rateShipping wiring lands for
   * this carrier+service-level.
   */
  signature?: string;
  /**
   * Identifier of the package set this rate row references (see
   * `RateShipping` shared contract). The same `packageSetId` is shared by every
   * `(carrier, serviceLevel)` rate row that consumes the same package plan;
   * stays opaque to the client (server picks the strings).
   */
  packageSetId?: string;

  constructor(
    carrier: string,
    serviceLevel: string,
    cost: number,
    deliveryDays: number,
    signature?: string,
    packageSetId?: string,
    deliveryDaysMin?: number,
    deliveryDaysMax?: number
  ) {
    this.carrier = carrier;
    this.serviceLevel = serviceLevel;
    this.cost = cost;
    this.deliveryDays = deliveryDays;
    this.signature = signature;
    this.packageSetId = packageSetId;
    this.deliveryDaysMin = deliveryDaysMin;
    this.deliveryDaysMax = deliveryDaysMax;
  }

  /**
   * Returns `[min, max]` if the range fields are populated, else
   * `[deliveryDays, deliveryDays]` if the legacy single value is
   * populated, else `null` when no transit estimate is available.
   * Both ends inclusive; a same-day range collapses to a single value
   * in the UI ("2 business days" rather than "2–2 business days").
   */
  getDeliveryDaysRange(): { min: number; max: number } | null {
    if (this.deliveryDaysMin != null && this.deliveryDaysMax != null) {
      return { min: this.deliveryDaysMin, max: this.deliveryDaysMax };
    }
    if (this.deliveryDaysMin != null) {
      return { min: this.deliveryDaysMin, max: this.deliveryDaysMin };
    }
    if (this.deliveryDaysMax != null) {
      return { min: this.deliveryDaysMax, max: this.deliveryDaysMax };
    }
    if (this.deliveryDays != null && !isNaN(this.deliveryDays) && this.deliveryDays > 0) {
      return { min: this.deliveryDays, max: this.deliveryDays };
    }
    return null;
  }
}
